import { Component, inject, OnDestroy } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { ZardDialogRef } from '../components/ui/dialog/dialog-ref';
import { injectDialogData } from '../components/ui/dialog/dialog.service';

/** Was der Benutzer entschieden hat. `cancel` heißt: hierbleiben. */
export type UnsavedDecision = 'save' | 'discard' | 'cancel';

export interface UnsavedChangesDialogData {
  /** Ergebnis des Dialogs. ZardUIs `ZardDialogRef` kennt kein `afterClosed()`. */
  onDecision: (decision: UnsavedDecision) => void;
  /** Womit die Arbeit weitergeht, wenn gespeichert wird — z. B. „Vorlage speichern". */
  saveLabel: string;
  /** Was das Speichern verhindert. Steht es hier, bleibt „Speichern" gesperrt. */
  blocked: string | undefined;
}

/**
 * Die Rückfrage vor dem Verlassen einer Ansicht mit offenen Änderungen.
 *
 * Drei Wege statt zwei: **Speichern** (und dann weiter), **Verwerfen** (weiter,
 * Änderungen weg) und **Hierbleiben**. `openConfirmDialog` kann das nicht — der
 * Dialog hat genau einen Bestätigungsknopf, und das Kreuz müsste dort dieselbe
 * Bedeutung tragen wie „Verwerfen". Genau das wäre die gefährliche Verwechslung.
 *
 * Deshalb bringt diese Komponente die Fußzeile selbst mit (`zHideFooter`). Das
 * Kreuz, Escape und der Klick daneben bedeuten alle **Hierbleiben**: Escape und
 * der Klick daneben schließen den Dialog ohne Rückruf, deshalb meldet
 * `ngOnDestroy` sie nach.
 */
@Component({
  selector: 'app-unsaved-changes-dialog',
  imports: [LucideAngularModule],
  template: `
    @if (blocked) {
      <p class="mb-3 flex items-start gap-1.5 text-[11.5px] leading-snug text-amber-700">
        <lucide-angular name="AlertTriangle" size="13" class="mt-px shrink-0"></lucide-angular>
        <span>Speichern geht gerade nicht — es fehlt noch: {{ blocked }}.</span>
      </p>
    }

    <div class="flex flex-wrap items-center justify-end gap-1.5">
      <button type="button" (click)="decide('cancel')"
        class="flex min-h-9 items-center rounded-none border border-neutral-200 px-3 text-[12.5px] text-neutral-700 transition hover:bg-neutral-50">
        Hierbleiben
      </button>
      <button type="button" (click)="decide('discard')"
        class="flex min-h-9 items-center gap-1.5 rounded-none border border-neutral-200 px-3 text-[12.5px] text-red-700 transition hover:border-red-300 hover:bg-red-50">
        <lucide-angular name="Trash2" size="14"></lucide-angular>
        Änderungen verwerfen
      </button>
      <button type="button" (click)="decide('save')" [disabled]="!!blocked"
        class="flex min-h-9 items-center gap-1.5 rounded-none bg-primary-600 px-3 text-[12.5px] font-medium text-white transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-neutral-200 disabled:text-neutral-400">
        <lucide-angular name="Save" size="14"></lucide-angular>
        {{ saveLabel }}
      </button>
    </div>
  `,
})
export class UnsavedChangesDialogComponent implements OnDestroy {

  private readonly _dialogRef: ZardDialogRef = inject(ZardDialogRef);
  private readonly _data: UnsavedChangesDialogData = injectDialogData<UnsavedChangesDialogData>();

  protected readonly saveLabel = this._data.saveLabel;
  protected readonly blocked = this._data.blocked;

  private _decided = false;

  protected decide(decision: UnsavedDecision): void {
    this._decided = true;
    this._data.onDecision(decision);
    this._dialogRef.close();
  }

  ngOnDestroy(): void {
    if (!this._decided) this._data.onDecision('cancel');
  }
}
